import { BatchRequestFactory, BatchItemProcessor, AxiosResponse } from 'core-utilities';
import { metricsService } from 'core-roblox-utilities';
import thumbnailMetaData from '../services/thumbnailMetaData';
import thumbnailUtil from './thumbnailUtil';
import {
  ThumbnailTypes,
  BatchRequestError,
  ThumbnailRequesters,
  ThumbnailDataItem,
  FeatureName,
  LoadFailureName,
  LoadSuccessMetricsType,
  LoadSuccessName,
  LoadFailureMetricsType,
  MetaData,
  ThumbnailQueueItem
} from '../constants/thumbnail2dConstant';

const { getThumbnailMetaData } = thumbnailMetaData;
const {
  getThumbnailRequesterProperties,
  getCachePropertiesFromMetaData,
  shouldLogMetrics
} = thumbnailUtil;

const batchRequestFactory = new BatchRequestFactory<ThumbnailQueueItem>();

const thumbnailRequesters: ThumbnailRequesters = {};

function thumbnailProcessorKeySerializer({
  targetId,
  type,
  size,
  format,
  isCircular
}: ThumbnailQueueItem): string {
  return `${targetId.toString()}:${type}:${size}:${format}:${isCircular ? 'circular' : 'regular'}`;
}

function thumbnailItemIdSerializer(item: ThumbnailQueueItem): string {
  const { type, targetId } = item;
  if (type === ThumbnailTypes.universeThumbnail || type === ThumbnailTypes.universeThumbnails) {
    return targetId.toString();
  }
  return thumbnailProcessorKeySerializer(item);
}

const getThumbnailRequester = (
  thumbnailRequestProcessor: BatchItemProcessor<ThumbnailQueueItem>,
  thumbnailRequesterKey: string,
  metaData?: MetaData
) => {
  if (thumbnailRequesterKey in thumbnailRequesters) {
    return thumbnailRequesters[thumbnailRequesterKey];
  }
  const processor = batchRequestFactory.createRequestProcessor(
    thumbnailRequestProcessor,
    item => thumbnailItemIdSerializer(item),
    getThumbnailRequesterProperties(batchRequestFactory, metaData)
  );
  thumbnailRequesters[thumbnailRequesterKey] = processor;
  return processor;
};

function processThumbnailBatchRequest(
  item: ThumbnailQueueItem,
  thumbnailRequestProcessor: BatchItemProcessor<ThumbnailQueueItem>,
  thumbnailRequesterKey: string = thumbnailProcessorKeySerializer(item),
  clearCachedValue: boolean
): Promise<ThumbnailDataItem> {
  const metaData = getThumbnailMetaData();
  const batchRequester = getThumbnailRequester(
    thumbnailRequestProcessor,
    thumbnailRequesterKey,
    metaData
  );
  if (clearCachedValue) {
    batchRequester.invalidateItem(item);
  }

  const cacheProperties = getCachePropertiesFromMetaData(metaData);
  return batchRequester
    .queueItem(item, null, cacheProperties)
    .then((data: ThumbnailDataItem) => {
      if (metricsService && data.performance && shouldLogMetrics(metaData)) {
        const {
          performance: { duration }
        } = data;
        metricsService
          .logMeasurement({
            featureName: FeatureName,
            measureName: LoadSuccessName,
            metricsType: LoadSuccessMetricsType,
            excludeCountry: true,
            value: duration
          })
          // eslint-disable-next-line no-console
          .catch(console.debug);
      }
      return data;
    })
    .catch((error: BatchRequestError) => {
      // eslint-disable-next-line no-console
      console.debug({ error });
      if (
        metricsService &&
        shouldLogMetrics(metaData) &&
        error === BatchRequestError.maxAttemptsReached
      ) {
        metricsService
          .logMeasurement({
            featureName: FeatureName,
            measureName: LoadFailureName,
            metricsType: LoadFailureMetricsType,
            excludeCountry: true
          })
          // eslint-disable-next-line no-console
          .catch(console.debug);
      }

      // chain the rejection so that other listeners get triggered.
      return Promise.reject(error);
    });
}

export default {
  processThumbnailBatchRequest,
  thumbnailItemIdSerializer
};
